import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import PaymentSchedulesList from './PaymentSchedulesList'
import ActivityLog from './ActivityLog'
import ActivityComposer from './ActivityComposer'
import { CLOSE_COLLECTION_CASE } from './queries'

function CloseCaseControl({ caseId, isClosed }) {
    const [open, setOpen] = useState(false)
    const [note, setNote] = useState('')
    const [errorMsg, setErrorMsg] = useState(null)

    const [closeCase, { loading }] = useMutation(CLOSE_COLLECTION_CASE, {
        refetchQueries: [
            'AllCollectionCases',
            'CollectionCasesCount',
            'CollectionAggregates',
            'CaseActivities',
        ],
    })

    if (isClosed) {
        return (
            <span className="cd-pill status-closed">
                <i className="fa fa-check" /> Case closed
            </span>
        )
    }

    const submit = () => {
        if (loading) return
        setErrorMsg(null)
        closeCase({
            variables: { caseId, note: note.trim() || null },
        })
            .then(({ data }) => {
                const r = data && data.closeCollectionCase
                if (r && r.ok) {
                    setOpen(false)
                    setNote('')
                } else {
                    const messages = r && r.errors ? r.errors.flatMap((e) => e.messages) : []
                    setErrorMsg(messages.join(' ') || 'Could not close case.')
                }
            })
            .catch((e) => setErrorMsg(e.message || 'Could not close case.'))
    }

    if (!open) {
        return (
            <button
                type="button"
                className="cd-btn danger-outline sm"
                onClick={() => setOpen(true)}
            >
                <i className="fa fa-times-circle" /> Close case
            </button>
        )
    }

    return (
        <div className="cd-closecase">
            <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Closing note (optional)…"
                rows={2}
                className="cd-composer-textarea"
                autoFocus
            />
            {errorMsg && <div className="cd-closecase-error">{errorMsg}</div>}
            <div className="cd-closecase-actions">
                <button
                    type="button"
                    className="cd-btn outline sm"
                    onClick={() => {
                        setOpen(false)
                        setNote('')
                        setErrorMsg(null)
                    }}
                    disabled={loading}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="cd-btn danger-outline sm"
                    onClick={submit}
                    disabled={loading}
                >
                    {loading ? 'Closing…' : 'Close case'}
                </button>
            </div>
        </div>
    )
}

export default function CaseDetailPanel({ collectionCase, focusActivity, onActivityFocused }) {
    const caseId = Number(collectionCase.id)
    const tags = collectionCase.driverTags ?? []
    const isClosed = collectionCase.status === 'CLOSED'
    return (
        <div className="cd-panel">
            <div className="cd-panel-head">
                {tags.length > 0 ? (
                    <div className="cd-panel-tags">
                        <span className="label">TAGS</span>
                        {tags.map((t, i) => (
                            <span
                                key={t.name || i}
                                className="cd-tag-chip"
                                style={{ background: t.color ? `#${t.color}` : '#6c757d' }}
                            >
                                {t.name}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span />
                )}
                <CloseCaseControl caseId={caseId} isClosed={isClosed} />
            </div>
            <div className="cd-panel-grid two-col">
                <div className="cd-card">
                    <PaymentSchedulesList driverId={collectionCase.driverId} />
                </div>
                <div className="cd-card">
                    <h3>Activity</h3>
                    <ActivityComposer
                        caseId={caseId}
                        focusOnMount={focusActivity}
                        onFocused={onActivityFocused}
                    />
                    <div className="cd-activity-divider" />
                    <ActivityLog caseId={caseId} />
                </div>
            </div>
        </div>
    )
}
